import type { ApiError } from '@condo/contracts';
import { useAuthStore } from '@/features/auth/auth.store';
import { env } from '@/lib/env';
import { ApiRequestError } from './errors';

const TOKEN_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry
let refreshPromise: Promise<void> | null = null;
let activeTenantId: string | null = null;

export function setActiveTenant(id: string | null): void {
  activeTenantId = id;
}

async function getValidToken(): Promise<string | null> {
  const store = useAuthStore.getState();
  const { session } = store;
  if (!session?.token) return null;

  const expiresAt = new Date(session.expiresAt).getTime();
  if (Date.now() + TOKEN_BUFFER_MS < expiresAt) return session.token;

  if (!refreshPromise) {
    refreshPromise = store.hydrate().finally(() => { refreshPromise = null; });
  }
  await refreshPromise;
  return useAuthStore.getState().session?.token ?? null;
}

/**
 * Base URL del API.
 *
 * Cuando `VITE_USE_MOCKS=true`: usamos `/api` como prefijo y MSW intercepta
 * todo. El browser nunca sale a la red.
 *
 * Cuando `VITE_USE_MOCKS=false`: usamos la URL real del HTTP API Gateway.
 * Las peticiones van por HTTPS al endpoint del stack Api.
 */
const API_BASE = env.useMocks ? '/api' : env.apiUrl;

interface RequestOptions<TBody> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: TBody;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

function buildUrl(path: string, query?: Record<string, string | number | undefined>) {
  // En modo mocks (path empieza con /api) usamos la origin del browser.
  // En modo real, API_BASE ya trae protocolo + host.
  const base = API_BASE.startsWith('http') ? API_BASE : window.location.origin + API_BASE;
  const url = new URL(
    API_BASE.startsWith('http') ? `${API_BASE}${path}` : `${API_BASE}${path}`,
    base,
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<TResp, TBody = unknown>(
  path: string,
  opts: RequestOptions<TBody> = {},
): Promise<TResp> {
  const token = await getValidToken();
  const session = useAuthStore.getState().session;
  const headers: Record<string, string> = {
    accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
    // El header x-user-id sirve al mock MSW para poblar el AppContext.
    // El backend real ignora estos headers y lee todo del JWT claim.
    // x-tenant-id y x-user-role ya no se envían: Task 8.2 añadirá
    // X-Acting-Tenant desde el contexto de la ruta activa.
    if (env.useMocks && session) {
      headers['x-user-id'] = session.userId;
    }
  }

  if (activeTenantId) {
    headers['x-acting-tenant'] = activeTenantId;
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) {
    return undefined as TResp;
  }

  const payload = (await res.json().catch(() => ({}))) as unknown;

  if (!res.ok) {
    throw new ApiRequestError(res.status, payload as ApiError);
  }

  return payload as TResp;
}

export const api = {
  get: <TResp>(path: string, opts?: Omit<RequestOptions<never>, 'body' | 'method'>) =>
    request<TResp>(path, { ...opts, method: 'GET' }),
  post: <TResp, TBody = unknown>(path: string, body?: TBody) =>
    request<TResp, TBody>(path, { method: 'POST', body }),
  patch: <TResp, TBody = unknown>(path: string, body?: TBody) =>
    request<TResp, TBody>(path, { method: 'PATCH', body }),
  put: <TResp, TBody = unknown>(path: string, body?: TBody) =>
    request<TResp, TBody>(path, { method: 'PUT', body }),
  delete: <TResp = void>(path: string) =>
    request<TResp>(path, { method: 'DELETE' }),
};
